import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarIcon, MapPin, Clock, ArrowRight, Filter } from 'lucide-react';
import { fetchEvents } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { format, isAfter, isBefore, isSameDay, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ImageHelper from '../lib/imageHelper';
import SeoHead from '@/components/Seo';

interface Ministry {
  id: number;
  name: string;
}

interface Event {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location: string;
  category?: string;
  photo: string;
  slug: string;
  ministry?: Ministry;
  ministry_id: string;
}

const Events = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMinistry, setSelectedMinistry] = useState<string>('All');
  const { toast } = useToast();

  // --- Helper: Sort Events Logic ---
  const sortEvents = (eventList: Event[]) => {
    const now = new Date();
    const upcoming: Event[] = [];
    const past: Event[] = [];
    const ongoing: Event[] = [];

    eventList.forEach(event => {
      const start = parseISO(event.start_date);
      const end = parseISO(event.end_date);

      if (isBefore(end, now)) {
        past.push(event);
      } else if (isAfter(start, now)) {
        upcoming.push(event);
      } else {
        ongoing.push(event);
      }
    });

    return { upcoming, ongoing, past };
  };

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await fetchEvents();
        setEvents(response.data.events);
      } catch (error) {
        toast({ title: "Note", description: "Updating event feed...", variant: "default" });
        setEvents([]); 
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [toast]);

  const ministries = ['All', ...Array.from(new Set(events.map(e => e.ministry?.name).filter(Boolean)))];
  
  const filteredEvents = selectedMinistry === 'All' 
    ? events 
    : events.filter(e => e.ministry?.name === selectedMinistry);

  const { upcoming, ongoing, past } = sortEvents(filteredEvents);
  const activeEvents = [...ongoing, ...upcoming];

  const getMinistryStyle = (id: string) => {
    const styles: Record<string, string> = {
      '1': 'bg-amber-100 text-amber-700 border-amber-200',
      '2': 'bg-blue-100 text-blue-700 border-blue-200',
      '3': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return styles[id] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
       <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin" />
          <p className="font-serif italic text-slate-500">Preparing the Calendar...</p>
       </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-20">
      <SeoHead title="Diocesan Calendar | Events" description="Stay informed with upcoming events in the Diocese of Ibadan South." />

      {/* Header Section */}
      <div className="bg-slate-900 text-white pt-24 pb-40 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 mb-4 px-4 py-1">Diocesan Life</Badge>
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6">Calendar & Events</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto font-serif italic leading-relaxed">
            "For everything there is a season, and a time for every matter under heaven."
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-24">
        {/* Sticky-ish Filter Bar */}
        <div className="bg-white p-2 rounded-3xl shadow-xl mb-12 flex items-center gap-2 overflow-x-auto no-scrollbar border border-slate-100">
          <div className="flex items-center gap-2 px-4 border-r border-slate-100 mr-2 text-slate-400">
            <Filter className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-widest hidden md:block">Filter</span>
          </div>
          {ministries.map((m) => (
            <button
              key={m}
              onClick={() => setSelectedMinistry(m)}
              className={`whitespace-nowrap px-6 py-2 rounded-2xl text-sm font-bold transition-all ${
                selectedMinistry === m ? 'bg-amber-600 text-white shadow-lg shadow-amber-600/20' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        {/* Active Events List */}
        <div className="space-y-8">
          <AnimatePresence mode="popLayout">
            {activeEvents.length > 0 ? (
              activeEvents.map((event) => {
                const startDate = parseISO(event.start_date);
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    layout
                  >
                    <Link to={`/events/${event.slug}`} className="group block">
                      <Card className="border-none shadow-sm hover:shadow-2xl transition-all duration-500 rounded-[2rem] overflow-hidden bg-white">
                        <div className="flex flex-col md:flex-row">
                          {/* Date Visual */}
                          <div className="md:w-32 bg-slate-50 border-r border-slate-100 flex flex-col items-center justify-center p-6 group-hover:bg-amber-600 transition-colors duration-500">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-400 group-hover:text-amber-200">{format(startDate, 'MMM')}</span>
                            <span className="text-4xl font-serif font-bold text-slate-900 group-hover:text-white">{format(startDate, 'dd')}</span>
                          </div>

                          {/* Image Thumbnail */}
                          <div className="md:w-64 h-48 md:h-auto relative overflow-hidden shrink-0">
                            <ImageHelper src={event.photo} alt={event.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            {isSameDay(startDate, new Date()) && (
                              <div className="absolute top-4 left-4">
                                <Badge className="bg-red-500 text-white border-none animate-pulse">Happening Today</Badge>
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 p-8">
                            <div className="flex flex-wrap items-center gap-3 mb-4">
                              <Badge variant="outline" className={getMinistryStyle(event.ministry_id)}>
                                {event.ministry?.name || 'General'}
                              </Badge>
                              <div className="flex items-center text-slate-400 text-xs font-bold uppercase tracking-tight">
                                <Clock className="h-3 w-3 mr-1 text-amber-500" />
                                {format(startDate, 'p')}
                              </div>
                            </div>
                            
                            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-3 group-hover:text-amber-700 transition-colors">
                              {event.title}
                            </h3>
                            
                            <p className="text-slate-500 line-clamp-2 text-sm mb-6 font-serif leading-relaxed">
                              {event.description}
                            </p>

                            <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-50">
                              <div className="flex items-center text-slate-500 text-sm">
                                <MapPin className="h-4 w-4 mr-2 text-slate-300" />
                                {event.location}
                              </div>
                              <div className="text-amber-600 font-bold text-sm flex items-center opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0">
                                View Details <ArrowRight className="h-4 w-4 ml-2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })
            ) : (
              <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-slate-100">
                <CalendarIcon className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-400 font-serif italic">No events found for this category.</p>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Past Events Section */}
        {past.length > 0 && (
          <div className="mt-32">
            <div className="flex items-center gap-4 mb-8">
              <h2 className="text-2xl font-serif font-bold text-slate-800">Archive</h2>
              <div className="h-px flex-1 bg-slate-200"></div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Past Records</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 opacity-75 hover:opacity-100 transition-opacity">
              {past.map(event => (
                <Link key={event.id} to={`/events/${event.slug}`}>
                  <Card className="border-none shadow-sm rounded-3xl overflow-hidden bg-white grayscale hover:grayscale-0 transition-all">
                    <div className="h-40 relative">
                       <ImageHelper src={event.photo} alt={event.title} className="w-full h-full object-cover" />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-2">{format(parseISO(event.start_date), 'MMMM yyyy')}</p>
                      <h4 className="font-bold text-slate-800 line-clamp-1">{event.title}</h4>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;